<template>
  <div class="then-row" :class="{ 'then-row--without-delete': !showDeleteAction, 'then-row--invalid': hasValidationError }">
    <span class="then-label">THEN</span>
    <custom-dropdown
      :list="targetFieldItems"
      :selected="selectedTargetField"
      :default-style="targetFieldStyle"
      prevent-deselection
      @select="updateAction('field', $event)"
    >
      <template #label="{ item, defaultLabel }">
        <div class="elipsis-text mr-4" v-overflow-tooltip>
          {{ displayOption(item) || defaultLabel }}
        </div>
      </template>
      <template #item="{ item }">
        <div class="elipsis-text list-name" v-overflow-tooltip>{{ displayOption(item) }}</div>
      </template>
    </custom-dropdown>
    <custom-dropdown
      :list="sourceTypeItems"
      :selected="selectedSourceType"
      :default-style="sourceTypeStyle"
      prevent-deselection
      @select="updateSourceType"
    />
    <input
      v-if="usesPluginCustomFieldInput"
      :value="sourceFieldInputValue"
      :style="sourceFieldInputStyle"
      class="source-field-input"
      type="text"
      :name="`map-source-field-${action.id}`"
      @input="updateSourceFieldText($event.target.value)"
    />
    <custom-dropdown
      v-else
      :list="sourceFieldItems"
      :selected="selectedSourceField"
      :default-style="sourceFieldStyle"
      prevent-deselection
      @select="updateAction('value', $event)"
    >
      <template #label="{ item, defaultLabel }">
        <div class="elipsis-text mr-4" v-overflow-tooltip>
          {{ displayOption(item) || defaultLabel }}
        </div>
      </template>
      <template #item="{ item }">
        <div class="elipsis-text list-name" v-overflow-tooltip>{{ displayOption(item) }}</div>
      </template>
    </custom-dropdown>
    <button v-if="showDeleteAction" class="icon-btn delete-action faint" type="button" @click="$emit('remove', action.id)">
      <delete-icon width="14px" height="14px" />
    </button>
    <p v-if="addAttemptError" class="then-row__error">
      Complete the target, source, and source field before adding another map.
    </p>
  </div>
</template>

<script>
import CustomDropdown from '@/components/common/customDropdown.vue'
import DeleteIcon from '@/components/common/icons/deleteIcon.vue'
import { clone } from './configModel'
import { getActionTargetItems, getActionValueItems, getComparableFieldType, getComparableFieldTypeLabel, isFieldTypeCompatible, optionDisplayLabel, optionValue, resolveActionOption, withValidationStyle } from './conditionHelper'
import { MAP_ACTION_MATERIAL_FIXED_FIELDS } from './configConstants'
import config from '@/config'

const MAP_ACTION_TYPE = { id: 'MAP', name: 'MAP', value: 'MAP' }
const NO_MATCHING_FIELDS_ID = '__NO_MATCHING_FIELDS__'

export default {
  name: 'MapActionRow',
  components: {
    CustomDropdown,
    DeleteIcon
  },
  props: {
    action: {
      type: Object,
      required: true
    },
    actions: {
      type: Array,
      default: () => []
    },
    lists: {
      type: Object,
      required: true
    },
    selectedTab: {
      type: Object,
      required: true
    },
    selectedConfig: {
      type: Object,
      required: true
    },
    validation: {
      type: Object,
      default: () => ({})
    },
    addAttemptError: {
      type: Boolean,
      default: false
    },
    showDeleteAction: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      dropdownStyles: {
        targetField: {
          dropDownWidth: '176px',
          borderColor: '#9ee6b8',
          backGroundColor: '#effdf4',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        },
        sourceType: {
          dropDownWidth: '176px',
          borderColor: '#f0f2f5',
          backGroundColor: '#f4f5f7',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        },
        sourceField: {
          dropDownWidth: '180px',
          borderColor: '#e1e7ef',
          backGroundColor: '#ffffff',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        }
      }
    }
  },
  computed: {
    targetFieldItems () {
      const items = getActionTargetItems({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.selectedConfig,
        actions: this.actions,
        actionId: this.action.id,
        actionType: 'MAP'
      })
      const filteredItems = (optionValue(this.selectedConfig) === 'STANDARD_MATERIAL_FORM'
        ? items.filter(item => item?.type !== config.FORM_TYPE.TABLE && item?.type !== config.FORM_TYPE.DATE && item?.type !== config.FORM_TYPE.TIME)
        : items).filter(item => !this.isErpIdField(item))
      const selectedTarget = resolveActionOption(filteredItems, this.action.field)

      return selectedTarget || !this.action.field
        ? filteredItems
        : [...filteredItems, this.action.field]
    },
    sourceTypeItems () {
      const baseItems = [
        ...(this.lists.configurationTypes || []),
        ...(this.lists.sourceFieldTypes || [])
      ].filter(item => optionValue(item) !== 'SEQUENCE_TEMPLATE')
      const uniqueItems = baseItems.filter((item, index, items) => {
        return index === items.findIndex(candidate => optionValue(candidate) === optionValue(item))
      })

      if (!optionValue(this.action.source) || optionValue(this.action.source) === 'SEQUENCE_TEMPLATE') {
        return uniqueItems
      }

      return resolveActionOption(uniqueItems, this.action.source)
        ? uniqueItems
        : [...uniqueItems, this.action.source]
    },
    selectedTargetField () {
      return resolveActionOption(this.targetFieldItems, this.action.field) || {}
    },
    selectedTargetFieldDefinition () {
      const selectedValue = optionValue(this.action.field)
      return this.targetFieldItems.find(item => optionValue(item) === selectedValue) ||
        (this.action.field && typeof this.action.field === 'object' ? this.action.field : {})
    },
    selectedTargetComparableType () {
      return getComparableFieldType(this.selectedTargetFieldDefinition)
    },
    selectedTargetCustomListId () {
      return this.selectedTargetFieldDefinition?.customListId ?? this.selectedTargetFieldDefinition?.custom_list_id ?? null
    },
    isTargetCustomListField () {
      return getComparableFieldType(this.selectedTargetFieldDefinition) === 'CUSTOM_LIST'
    },
    selectedTargetCompatibilityKey () {
      return `${this.selectedTargetComparableType || ''}:${this.selectedTargetCustomListId || ''}`
    },
    selectedTargetComparableTypeLabel () {
      return getComparableFieldTypeLabel(this.selectedTargetComparableType)
    },
    selectedSourceType () {
      return resolveActionOption(this.sourceTypeItems, this.action.source) || {}
    },
    usesPluginCustomFieldInput () {
      return optionValue(this.selectedSourceType) === 'PLUGIN_CUSTOM_FIELDS'
    },
    sourceFieldRawItems () {
      if (!optionValue(this.selectedSourceType) || this.usesPluginCustomFieldInput) {
        return []
      }

      if (optionValue(this.selectedSourceType) === 'MATERIAL_FIXED_FIELDS') {
        return MAP_ACTION_MATERIAL_FIXED_FIELDS
      }

      return getActionValueItems({
        selectedConfig: this.selectedSourceType,
        lists: this.lists
      })
    },
    sourceFieldCompatibleItems () {
      if (!this.selectedTargetComparableType) {
        return this.sourceFieldRawItems
      }

      return this.sourceFieldRawItems.filter(item => {
        if (!isFieldTypeCompatible(this.selectedTargetFieldDefinition, item)) {
          return false
        }

        if (!this.isTargetCustomListField) {
          return true
        }

        const sourceCustomListId = item?.customListId ?? item?.custom_list_id ?? null
        return sourceCustomListId !== null && `${sourceCustomListId}` === `${this.selectedTargetCustomListId}`
      })
    },
    shouldFilterSourceFieldsForCycles () {
      return optionValue(this.selectedSourceType) === optionValue(this.selectedConfig)
    },
    cycleBlockedSourceFieldIds () {
      if (!this.shouldFilterSourceFieldsForCycles) {
        return []
      }

      return this.actions
        .filter(item => optionValue(item?.type) === 'MAP')
        .map(item => optionValue(item?.field))
        .filter(Boolean)
    },
    duplicateSourceFieldIdsForSelectedCategory () {
      const selectedSourceTypeId = optionValue(this.selectedSourceType)

      if (!selectedSourceTypeId || this.usesPluginCustomFieldInput) {
        return []
      }

      return this.actions
        .filter(item => item.id !== this.action.id && optionValue(item?.type) === 'MAP')
        .filter(item => optionValue(item?.source) === selectedSourceTypeId)
        .map(item => optionValue(item?.value))
        .filter(Boolean)
    },
    filteredSourceFieldCompatibleItems () {
      const cycleBlockedIds = new Set(this.cycleBlockedSourceFieldIds)
      const duplicateSourceIds = new Set(this.duplicateSourceFieldIdsForSelectedCategory)

      return this.sourceFieldCompatibleItems.filter(item => {
        const itemId = optionValue(item)

        if (this.shouldFilterSourceFieldsForCycles && cycleBlockedIds.has(itemId)) {
          return false
        }

        return !duplicateSourceIds.has(itemId)
      })
    },
    sourceFieldItems () {
      if (!optionValue(this.selectedSourceType) || this.usesPluginCustomFieldInput) {
        return []
      }

      const filteredItems = this.filteredSourceFieldCompatibleItems
      const selectedSourceField = resolveActionOption(this.sourceFieldCompatibleItems, this.action.value)

      if (filteredItems.length > 0 || !this.selectedTargetComparableType) {
        return selectedSourceField && !resolveActionOption(filteredItems, this.action.value)
          ? [...filteredItems, selectedSourceField]
          : filteredItems
      }

      return [{
        id: NO_MATCHING_FIELDS_ID,
        value: NO_MATCHING_FIELDS_ID,
        name: 'No matching fields available'
      }]
    },
    selectedSourceField () {
      if (this.usesPluginCustomFieldInput) {
        return {}
      }

      const selectedSourceField = resolveActionOption(this.sourceFieldItems, this.action.value) ||
        resolveActionOption(this.sourceFieldCompatibleItems, this.action.value)

      if (selectedSourceField) {
        return selectedSourceField
      }

      if (this.action.value && typeof this.action.value === 'object') {
        return this.action.value
      }

      if (this.sourceFieldItems.length === 1 && optionValue(this.sourceFieldItems[0]) === NO_MATCHING_FIELDS_ID) {
        return this.sourceFieldItems[0]
      }

      return {}
    },
    targetFieldStyle () {
      return withValidationStyle(this.dropdownStyles.targetField, this.validation.field)
    },
    sourceTypeStyle () {
      return withValidationStyle(this.dropdownStyles.sourceType, this.validation.source)
    },
    sourceFieldStyle () {
      return withValidationStyle(this.dropdownStyles.sourceField, this.validation.value)
    },
    sourceFieldInputValue () {
      if (typeof this.action.value === 'string') {
        return this.action.value
      }

      return optionValue(this.action.value) || ''
    },
    sourceFieldInputStyle () {
      const baseStyle = withValidationStyle(this.dropdownStyles.sourceField, this.validation.value)
      return {
        width: '100%',
        minWidth: 0,
        height: baseStyle.labelHeight,
        border: `1px solid ${baseStyle.borderColor}`,
        backgroundColor: baseStyle.backGroundColor,
        borderRadius: '4px',
        padding: '0 12px',
        fontSize: baseStyle.ddLabelFontSize,
        outline: 'none'
      }
    },
    hasValidationError () {
      return Object.values(this.validation || {}).some(Boolean)
    }
  },
  watch: {
    selectedTargetCompatibilityKey (nextKey, previousKey) {
      if (this.usesPluginCustomFieldInput) {
        return
      }

      if (nextKey === previousKey) {
        return
      }

      const selectedValue = optionValue(this.action.value)
      if (!selectedValue) {
        return
      }

      const hasCompatibleSelection = Boolean(resolveActionOption(this.sourceFieldCompatibleItems, this.action.value))
      if (hasCompatibleSelection) {
        return
      }

      const next = clone(this.action)
      next.type = MAP_ACTION_TYPE
      next.value = ''
      this.$emit('update', next)
    },
    selectedSourceType: {
      handler (nextSource, previousSource) {
        if (optionValue(nextSource) === optionValue(previousSource)) {
          return
        }

        const selectedValue = optionValue(this.action.value)
        if (!selectedValue) {
          return
        }

        const hasCompatibleSelection = Boolean(resolveActionOption(this.sourceFieldCompatibleItems, this.action.value))
        if (hasCompatibleSelection) {
          return
        }

        const next = clone(this.action)
        next.type = MAP_ACTION_TYPE
        next.value = ''
        this.$emit('update', next)
      },
      deep: true
    }
  },
  methods: {
    isErpIdField (item) {
      return this.displayOption(item)?.trim()?.toLowerCase() === 'erp material id'
    },
    displayOption (value) {
      return optionDisplayLabel(value)
    },
    updateAction (key, value) {
      const next = clone(this.action)
      next.type = MAP_ACTION_TYPE
      next[key] = value
      this.$emit('update', next)
    },
    updateSourceType (value) {
      const next = clone(this.action)
      next.type = MAP_ACTION_TYPE
      next.source = value
      next.value = ''
      this.$emit('update', next)
    },
    updateSourceFieldText (value) {
      const next = clone(this.action)
      next.type = MAP_ACTION_TYPE
      next.value = value
      this.$emit('update', next)
    }
  }
}
</script>

<style lang="scss" scoped>
.then-row {
  display: grid;
  align-items: center;
  grid-template-columns: 40px 176px 176px minmax(180px, 1fr) 24px;
  gap: 8px;
  margin-bottom: 12px;
  position: relative;
}

.then-row.then-row--without-delete {
  grid-template-columns: 40px 176px 176px minmax(180px, 1fr);
}

.then-row.then-row--invalid {
  padding-bottom: 28px;
}

.then-label {
  color: #00a651;
  font-size: var(--text-xs2);
  font-weight: 600;
}

.icon-btn {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.delete-action:hover {
  background: #fff1f0;
}

.delete-action:hover ::v-deep .delete-icon {
  color: #f04438;
}

.faint {
  opacity: 0.65;
}

.source-field-input:focus {
  border-color: #9ee6b8 !important;
}

.then-row__error {
  position: absolute;
  left: 40px;
  bottom: 4px;
  margin: 0;
  color: #dc2626;
  font-size: var(--text-xs2);
  font-weight: 500;
}

::v-deep .custom-dropdown-container,
::v-deep .custom-select {
  width: 100%;
  max-width: 100%;
}

@media (max-width: 900px) {
  .then-row {
    grid-template-columns: 1fr;
  }

  .then-label {
    display: none;
  }

  .then-row__error {
    position: static;
  }
}
</style>
